import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  Shield,
  UserCog,
  Users,
  Plus,
  Search,
  MoreHorizontal,
  KeyRound,
  Trash2,
  ShieldOff,
  SlidersHorizontal,
  X,
  ArrowUp,
  ArrowDown,
  ChevronsUpDown,
} from "lucide-react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useIsSuperadmin } from "@/hooks/useUserRole";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { logSuperadminAction } from "@/lib/superadminAudit";
import { useColumnWidths, ColumnResizer } from "@/components/common/useColumnWidths";
import { BadgeOverflow } from "@/components/common/BadgeOverflow";
import { UserIdentityCell } from "@/components/users/UserIdentityCell";
import { adminFunctionError } from "@/lib/adminFunctionError";


interface PlatformUser {
  id: string;
  email: string | null;
  full_name?: string | null;
  created_at: string;
  last_sign_in_at: string | null;
  is_superadmin: boolean;
  companies: { id: string; name: string; role: string }[];
}

type RoleFilter = "superadmin" | "company_admin" | "standard";
type CompanyFilter = "assigned" | "unassigned";
type SortKey = "email" | "role" | "created_at" | "last_sign_in_at";
type ColumnKey = SortKey | "companies";

const DEFAULT_WIDTHS: Record<ColumnKey, number> = {
  email: 260,
  role: 140,
  companies: 260,
  created_at: 125,
  last_sign_in_at: 125,
};

/** How many company pills fit inline for a given Companies column width. */
const companyBadgeCapacity = (width: number) => Math.max(1, Math.floor((width - 56) / 130));

const fmt = (v?: string | null) => (v ? new Date(v).toLocaleDateString() : "—");

/** Display role for the platform Users page: superadmin, company admin (of any company), or standard. */
const platformRoleOf = (u: PlatformUser): RoleFilter =>
  u.is_superadmin
    ? "superadmin"
    : u.companies.some((c) => c.role === "admin")
      ? "company_admin"
      : "standard";

const ROLE_LABELS: Record<RoleFilter, string> = {
  superadmin: "Superadmin",
  company_admin: "Company admin",
  standard: "Standard user",
};


export default function PlatformUsers() {
  const { isSuperadmin, isLoading } = useIsSuperadmin();
  const { user, isLoading: authLoading } = useAuth();

  const [users, setUsers] = useState<PlatformUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [roleFilters, setRoleFilters] = useState<RoleFilter[]>([]);
  const [companyFilters, setCompanyFilters] = useState<CompanyFilter[]>([]);
  const [companyNameFilters, setCompanyNameFilters] = useState<string[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>("email");
  const [sortAsc, setSortAsc] = useState(true);

  const { widths, startResize } = useColumnWidths<ColumnKey>(
    "platform-users-column-widths",
    DEFAULT_WIDTHS,
    { min: 100, max: 720 }
  );
  const totalWidth =
    Object.values(widths).reduce((a: number, b) => a + (b as number), 0) + 56;


  const toggle = <T,>(list: T[], value: T, set: (v: T[]) => void) =>
    set(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

  const [grantOpen, setGrantOpen] = useState(false);
  const [grantEmail, setGrantEmail] = useState("");

  const [resetTarget, setResetTarget] = useState<PlatformUser | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PlatformUser | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [revokeTarget, setRevokeTarget] = useState<PlatformUser | null>(null);

  // Fallback: compose the directory from the basic account list plus the
  // membership/role tables (superadmins may read all of these).
  const loadComposed = async (): Promise<PlatformUser[]> => {
    const { data, error } = await supabase.functions.invoke("admin-users", {
      body: { action: "list" },
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    const accounts: any[] = data?.users ?? [];

    const [memberships, companies, superRole] = await Promise.all([
      supabase.from("user_companies").select("user_id, company_id, role"),
      supabase.from("companies").select("id, name"),
      supabase.from("custom_roles").select("id").eq("name", "superadmin").maybeSingle(),
    ]);

    let superIds = new Set<string>();
    if (superRole.data?.id) {
      const { data: links } = await supabase
        .from("user_custom_roles")
        .select("user_id")
        .eq("role_id", superRole.data.id);
      superIds = new Set((links ?? []).map((l) => l.user_id));
    }

    const names = new Map((companies.data ?? []).map((c) => [c.id, c.name]));
    const byUser = new Map<string, { id: string; name: string; role: string }[]>();
    for (const m of memberships.data ?? []) {
      const list = byUser.get(m.user_id) ?? [];
      list.push({
        id: m.company_id,
        name: names.get(m.company_id) ?? "Unknown company",
        role: m.role ?? "user",
      });
      byUser.set(m.user_id, list);
    }

    return accounts.map((u) => ({
      id: u.id,
      email: u.email ?? null,
      full_name: u.full_name ?? null,
      created_at: u.created_at,
      last_sign_in_at: u.last_sign_in_at ?? null,
      is_superadmin: superIds.has(u.id),
      companies: byUser.get(u.id) ?? [],
    }));
  };

  const load = async () => {
    setLoading(true);
    try {
      let list: PlatformUser[] | null = null;
      try {
        const { data, error } = await supabase.functions.invoke("admin-users", {
          body: { action: "list_platform_users" },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        if (Array.isArray(data?.users)) list = data.users as PlatformUser[];
      } catch {
        list = null;
      }
      if (!list) list = await loadComposed();
      list.sort((a, b) => (a.email ?? "").localeCompare(b.email ?? ""));
      setUsers(list);
    } catch (err: any) {
      toast.error(err.message ?? "Could not load users");
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (isSuperadmin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperadmin]);

  const superadminCount = users.filter((u) => u.is_superadmin).length;
  const companyAdminCount = users.filter((u) => platformRoleOf(u) === "company_admin").length;
  const standardCount = users.length - superadminCount - companyAdminCount;

  const allCompanyNames = useMemo(
    () =>
      Array.from(new Set(users.flatMap((u) => u.companies.map((c) => c.name)))).sort((a, b) =>
        a.localeCompare(b),
      ),
    [users],
  );

  const activeFilterCount =
    roleFilters.length + companyFilters.length + companyNameFilters.length;

  const resetFilters = () => {
    setRoleFilters([]);
    setCompanyFilters([]);
    setCompanyNameFilters([]);
  };

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = users.filter((u) => {
      if (roleFilters.length && !roleFilters.includes(platformRoleOf(u))) return false;
      if (companyFilters.length) {
        const status: CompanyFilter = u.companies.length ? "assigned" : "unassigned";
        if (!companyFilters.includes(status)) return false;
      }
      if (companyNameFilters.length) {
        if (!u.companies.some((c) => companyNameFilters.includes(c.name))) return false;
      }
      if (!q) return true;
      return (
        (u.email ?? "").toLowerCase().includes(q) ||
        (u.full_name ?? "").toLowerCase().includes(q) ||
        u.companies.some((c) => c.name.toLowerCase().includes(q))
      );
    });

    const dir = sortAsc ? 1 : -1;
    return [...filtered].sort((a, b) => {
      switch (sortKey) {
        case "role": {
          const rank = { superadmin: 0, company_admin: 1, standard: 2 } as const;
          return (rank[platformRoleOf(a)] - rank[platformRoleOf(b)]) * dir;
        }
        case "created_at":
          return (
            (new Date(a.created_at).getTime() - new Date(b.created_at).getTime()) * dir
          );
        case "last_sign_in_at":
          return (
            (new Date(a.last_sign_in_at ?? 0).getTime() -
              new Date(b.last_sign_in_at ?? 0).getTime()) *
            dir
          );
        default:
          return (a.email ?? "").localeCompare(b.email ?? "") * dir;
      }
    });
  }, [users, query, roleFilters, companyFilters, companyNameFilters, sortKey, sortAsc]);

  const superRoleId = async () => {
    const { data } = await supabase
      .from("custom_roles")
      .select("id")
      .eq("name", "superadmin")
      .maybeSingle();
    if (!data?.id) throw new Error("Superadmin role not found");
    return data.id;
  };

  const handleGrant = async () => {
    const email = grantEmail.trim();
    if (!email) return;
    setBusy(true);
    try {
      const existing = users.find((u) => (u.email ?? "").toLowerCase() === email.toLowerCase());
      let targetId = existing?.id;
      let invited = false;
      if (!targetId) {
        const { data, error } = await supabase.functions.invoke("admin-users", {
          body: { action: "invite", email },
        });
        if (error) throw await adminFunctionError(error);
        if (data?.error) throw new Error(data.error);
        if (!data?.user?.id) throw new Error("Invite did not return a user");
        targetId = data.user.id;
        invited = true;
      } else if (existing?.is_superadmin) {
        toast.info(`${email} is already a superadmin`);
        setGrantOpen(false);
        setGrantEmail("");
        return;
      }
      const { data, error } = await supabase.functions.invoke("admin-users", {
        body: { action: "grant_superadmin", userId: targetId },
      });
      if (error) throw await adminFunctionError(error);
      if (data?.error) throw new Error(data.error);
      await logSuperadminAction({
        action: "superadmin_granted",
        targetUser: { id: targetId, email },
        metadata: { invited },
      });
      toast.success(
        invited
          ? `Invitation sent — ${email} becomes a superadmin once they accept`
          : `${email} is now a superadmin`,
      );
      setGrantOpen(false);
      setGrantEmail("");
      await load();
    } catch (err: any) {
      toast.error(err.message ?? "Could not grant superadmin");
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    if (superadminCount <= 1) {
      toast.error("Cannot revoke the last superadmin");
      return;
    }
    setBusy(true);
    try {
      const roleId = await superRoleId();
      const { error } = await supabase
        .from("user_custom_roles")
        .delete()
        .eq("user_id", revokeTarget.id)
        .eq("role_id", roleId);
      if (error) throw error;
      await logSuperadminAction({
        action: "superadmin_revoked",
        targetUser: { id: revokeTarget.id, email: revokeTarget.email },
        metadata: { self: revokeTarget.id === user?.id },
      });
      toast.success("Superadmin access revoked");
      setRevokeTarget(null);
      await load();
    } catch (err: any) {
      toast.error(err.message ?? "Could not revoke superadmin");
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async () => {
    if (!resetTarget) return;
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-users", {
        body: {
          action: "send_password_reset",
          userId: resetTarget.id,
          redirectTo: `${window.location.origin}/reset-password`,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success(`Password reset email sent to ${resetTarget.email}`);
      setResetTarget(null);
    } catch (err: any) {
      toast.error(err.message ?? "Could not send reset email");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-users", {
        body: { action: "delete", userId: deleteTarget.id },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      await logSuperadminAction({
        action: "user_deleted",
        targetUser: { id: deleteTarget.id, email: deleteTarget.email },
        metadata: {
          companies: deleteTarget.companies.map((c) => c.name),
          company_ids: deleteTarget.companies.map((c) => c.id),
        },
      });
      toast.success(`${deleteTarget.email} was permanently deleted`);
      setDeleteTarget(null);
      setDeleteConfirm("");
      await load();
    } catch (err: any) {
      toast.error(err.message ?? "Could not delete user");
    } finally {
      setBusy(false);
    }
  };

  const SortHead = ({
    label,
    column,
    className,
  }: {
    label: string;
    column: SortKey;
    className?: string;
  }) => {
    const active = sortKey === column;
    return (
      <TableHead className={`relative border-r border-border/60 ${className ?? ""}`}>
        <button
          type="button"
          className="inline-flex items-center gap-1 hover:text-foreground max-w-full"
          onClick={() => {
            if (active) setSortAsc(!sortAsc);
            else {
              setSortKey(column);
              setSortAsc(true);
            }
          }}
        >
          <span className="truncate">{label}</span>
          {active ? (
            sortAsc ? (
              <ArrowUp className="w-3 h-3 shrink-0" />
            ) : (
              <ArrowDown className="w-3 h-3 shrink-0" />
            )
          ) : (
            <ChevronsUpDown className="w-3 h-3 opacity-40 shrink-0" />
          )}
        </button>
        <ColumnResizer onPointerDown={(e) => startResize(column, e)} />
      </TableHead>
    );
  };


  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }
  if (!isSuperadmin) return <Navigate to="/admin" replace />;

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-4 sm:px-6 pt-8 pb-6">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <Users className="w-5 h-5 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">Users</h1>
          <Badge variant="secondary">Platform-scoped</Badge>
        </div>
        <p className="text-muted-foreground">
          Every account on the platform: their companies, platform access, and account actions.
        </p>
      </div>

      <div className="px-4 sm:px-6 space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search email or company"
                className="pl-8 w-full sm:w-72"
              />
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm">
                  <SlidersHorizontal className="w-4 h-4 mr-1" />
                  Filters
                  {activeFilterCount > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {activeFilterCount}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-64 max-h-[26rem] overflow-y-auto">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase text-muted-foreground">
                      Platform role
                    </p>
                    {(
                      [
                        ["superadmin", `Superadmin (${superadminCount})`],
                        ["company_admin", `Company admin (${companyAdminCount})`],
                        ["standard", `Standard user (${standardCount})`],
                      ] as [RoleFilter, string][]
                    ).map(([value, label]) => (
                      <label key={value} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={roleFilters.includes(value)}
                          onCheckedChange={() => toggle(roleFilters, value, setRoleFilters)}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase text-muted-foreground">
                      Company status
                    </p>
                    {(
                      [
                        ["assigned", "Assigned to a company"],
                        ["unassigned", "No company"],
                      ] as [CompanyFilter, string][]
                    ).map(([value, label]) => (
                      <label key={value} className="flex items-center gap-2 text-sm">
                        <Checkbox
                          checked={companyFilters.includes(value)}
                          onCheckedChange={() => toggle(companyFilters, value, setCompanyFilters)}
                        />
                        {label}
                      </label>
                    ))}
                  </div>
                  {allCompanyNames.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold uppercase text-muted-foreground">
                        Company
                      </p>
                      {allCompanyNames.map((name) => (
                        <label key={name} className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={companyNameFilters.includes(name)}
                            onCheckedChange={() =>
                              toggle(companyNameFilters, name, setCompanyNameFilters)
                            }
                          />
                          <span className="truncate">{name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  {activeFilterCount > 0 && (
                    <Button variant="ghost" size="sm" className="w-full" onClick={resetFilters}>
                      Reset all
                    </Button>
                  )}
                </div>
              </PopoverContent>
            </Popover>

            {roleFilters.map((r) => (
              <Badge key={r} variant="secondary" className="gap-1">
                Role: {ROLE_LABELS[r]}
                <button onClick={() => toggle(roleFilters, r, setRoleFilters)} aria-label="Remove filter">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
            {companyFilters.map((c) => (
              <Badge key={c} variant="secondary" className="gap-1">
                Status: {c === "assigned" ? "Assigned" : "No company"}
                <button onClick={() => toggle(companyFilters, c, setCompanyFilters)} aria-label="Remove filter">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
            {companyNameFilters.map((c) => (
              <Badge key={c} variant="secondary" className="gap-1">
                {c}
                <button
                  onClick={() => toggle(companyNameFilters, c, setCompanyNameFilters)}
                  aria-label="Remove filter"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
            {activeFilterCount > 0 && (
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                Reset all
              </Button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground whitespace-nowrap">
              {visible.length} of {users.length}
            </span>
            <Button size="sm" onClick={() => setGrantOpen(true)}>
              <Plus className="w-4 h-4 mr-1" /> Grant superadmin
            </Button>
          </div>
        </div>

        <div className="bg-card rounded-xl border border-border overflow-x-auto">
          <Table className="table-fixed" style={{ minWidth: totalWidth }}>
            <colgroup>
              <col style={{ width: widths.email }} />
              <col style={{ width: widths.role }} />
              <col style={{ width: widths.companies }} />
              <col style={{ width: widths.created_at }} />
              <col style={{ width: widths.last_sign_in_at }} />
              <col style={{ width: 56 }} />
            </colgroup>
            <TableHeader className="sticky top-0 z-10 bg-card">
              <TableRow>
                <SortHead label="User" column="email" />
                <SortHead label="Platform role" column="role" />
                <TableHead className="relative border-r border-border/60">
                  <span className="truncate block">Companies</span>
                  <ColumnResizer onPointerDown={(e) => startResize("companies", e)} />
                </TableHead>
                <SortHead label="Registered" column="created_at" />
                <SortHead label="Last sign-in" column="last_sign_in_at" />
                <TableHead className="sticky right-0 z-20 bg-card shadow-[-4px_0_8px_-2px_rgba(0,0,0,0.06)]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-muted-foreground py-8">
                    Loading…

                  </TableCell>
                </TableRow>
              )}
              {!loading && visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-muted-foreground py-8">
                    No users match this view.
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                visible.map((u) => (
                  <TableRow key={u.id} className="group hover:bg-muted/50 h-12">
                    <TableCell className="py-0 border-r border-border/40">
                      <div className="flex items-center gap-2 min-w-0">
                        <UserIdentityCell id={u.id} name={u.full_name} email={u.email} />
                        {u.id === user?.id && (
                          <Badge variant="outline" className="shrink-0">
                            you
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-0 whitespace-nowrap overflow-hidden border-r border-border/40">
                      {(() => {
                        const role = platformRoleOf(u);
                        if (role === "superadmin")
                          return (
                            <Badge className="gap-1">
                              <Shield className="w-3 h-3" /> Superadmin
                            </Badge>
                          );
                        if (role === "company_admin")
                          return (
                            <Badge variant="secondary" className="gap-1">
                              <UserCog className="w-3 h-3" /> Company admin
                            </Badge>
                          );
                        return <span className="text-sm text-muted-foreground">Standard user</span>;
                      })()}
                    </TableCell>
                    <TableCell className="py-0 overflow-hidden border-r border-border/40">
                      <BadgeOverflow
                        visibleCount={companyBadgeCapacity(widths.companies)}
                        items={u.companies.map((c) => ({
                          id: c.id,
                          label: c.name,
                          hint: c.role === "admin" ? "admin" : "user",
                        }))}
                        empty={
                          <Badge variant="outline" className="text-muted-foreground">
                            No company
                          </Badge>
                        }
                      />
                    </TableCell>
                    <TableCell className="py-0 whitespace-nowrap overflow-hidden text-sm text-muted-foreground border-r border-border/40">
                      {fmt(u.created_at)}
                    </TableCell>
                    <TableCell className="py-0 whitespace-nowrap overflow-hidden text-sm text-muted-foreground border-r border-border/40">
                      {fmt(u.last_sign_in_at)}
                    </TableCell>

                    <TableCell className="sticky right-0 z-10 bg-card shadow-[-4px_0_8px_-2px_rgba(0,0,0,0.06)] group-hover:bg-muted/50">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" aria-label="Actions">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setResetTarget(u)}>
                            <KeyRound className="w-4 h-4 mr-2" /> Send reset email
                          </DropdownMenuItem>
                          {u.is_superadmin ? (
                            <DropdownMenuItem onClick={() => setRevokeTarget(u)}>
                              <ShieldOff className="w-4 h-4 mr-2" /> Revoke superadmin
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={async () => {
                                setBusy(true);
                                try {
                                  const { data, error } = await supabase.functions.invoke("admin-users", {
                                    body: { action: "grant_superadmin", userId: u.id },
                                  });
                                  if (error) throw await adminFunctionError(error);
                                  if (data?.error) throw new Error(data.error);
                                  await logSuperadminAction({
                                    action: "superadmin_granted",
                                    targetUser: { id: u.id, email: u.email },
                                  });
                                  toast.success(`${u.email} is now a superadmin`);
                                  await load();
                                } catch (err: any) {
                                  toast.error(err.message ?? "Could not grant superadmin");
                                } finally {
                                  setBusy(false);
                                }
                              }}
                            >
                              <Shield className="w-4 h-4 mr-2" /> Make superadmin
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            disabled={u.id === user?.id}
                            onClick={() => {
                              setDeleteConfirm("");
                              setDeleteTarget(u);
                            }}
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Delete account
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Grant superadmin */}
      <Dialog open={grantOpen} onOpenChange={setGrantOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Grant superadmin access</DialogTitle>
            <DialogDescription>
              Existing accounts are upgraded immediately. Unknown email addresses receive an
              invitation and become superadmin once they accept. If they don't belong to a
              company yet, they're added to Test Company Netherlands.
            </DialogDescription>
          </DialogHeader>
          <Input
            type="email"
            value={grantEmail}
            onChange={(e) => setGrantEmail(e.target.value)}
            placeholder="ops@fixid.com"
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrantOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleGrant} disabled={busy || !grantEmail.trim()}>
              Grant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke superadmin */}
      <Dialog open={!!revokeTarget} onOpenChange={(o) => !o && setRevokeTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke superadmin from {revokeTarget?.email}?</DialogTitle>
            <DialogDescription>
              {revokeTarget?.id === user?.id
                ? "This is your own account — you will immediately lose access to platform settings."
                : "They keep their account and company access, but lose all platform-wide control."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeTarget(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRevoke} disabled={busy}>
              Revoke
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send reset email */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => !o && setResetTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send password reset email?</DialogTitle>
            <DialogDescription>
              {resetTarget?.email} receives a secure, time-limited link and chooses a new password
              themselves. Their current password keeps working until they use the link.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetTarget(null)}>
              Cancel
            </Button>
            <Button onClick={handleReset} disabled={busy}>
              Send email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete account */}
      <Dialog
        open={!!deleteTarget}
        onOpenChange={(o) => {
          if (!o) {
            setDeleteTarget(null);
            setDeleteConfirm("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete this account permanently?</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  <span className="font-medium text-foreground">{deleteTarget?.email}</span> will be
                  removed from the platform entirely: sign-in, sessions and all company memberships
                  are revoked. This cannot be undone.
                </p>
                {deleteTarget && deleteTarget.companies.length > 0 ? (
                  <p>
                    This user belongs to {deleteTarget.companies.length}{" "}
                    {deleteTarget.companies.length === 1 ? "company" : "companies"}:{" "}
                    <span className="text-foreground">
                      {deleteTarget.companies.map((c) => c.name).join(", ")}
                    </span>
                    . If you only want to end access to one of them, remove them from that company
                    instead.
                  </p>
                ) : (
                  <p>This user does not belong to any company.</p>
                )}
                <p>Type the email address to confirm.</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <Input
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            placeholder={deleteTarget?.email ?? ""}
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={busy || deleteConfirm.trim().toLowerCase() !== (deleteTarget?.email ?? "").toLowerCase()}
            >
              Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
